import React, { useMemo, useState } from 'react';
import { Banknote, X } from 'lucide-react';
import { Invoice, InvoicePayment, PaymentMethod } from '../../types';
import { Button } from '../Button';
import {
    addPayment,
    addPaymentWithRemainder,
    PaymentValidationError,
} from '../../services/invoicePayments';
import {
    checkRemainder,
    isRemainderMethod,
    paymentRemainder,
    RemainderKind,
} from '../../services/paymentRemainder';
import { fetchInvoice } from '../../services/invoiceService';
import { openAmountCents, validatePayment, ValidationMessage } from '../../services/invoiceLogic';
import { centsFromEuroInput, formatCents, formatCentsPlain } from '../../services/money';
import { METHOD_LABEL } from './paymentBooking';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { RemainderOption } from './RemainderOption';

/**
 * "Als bezahlt markieren" aus der Belegliste heraus.
 *
 * Fachlich passiert hier nichts anderes als im Zahlungsbereich des Editors: es
 * wird eine Zahlung gebucht. Den Zahlungsstatus setzt auch von hier aus NIEMAND
 * im Browser — er entsteht im Datenbank-Trigger aus der Summe der Zahlungen.
 * Deshalb wird der Beleg nach dem Buchen frisch nachgeladen und so an die Liste
 * zurueckgegeben.
 *
 * Der Unterschied zum Weg ueber den Editor ist allein die Vorbelegung: Betrag =
 * offener Rest, Wertstellung = heute, Eingang per Ueberweisung. Das ist der
 * Fall, fuer den dieser Weg gebaut ist — der Kontoauszug zeigt den vollen
 * Betrag, und dafuer muss der Beleg nicht geoeffnet werden. Jedes Feld bleibt
 * aenderbar, damit die Teilzahlung nicht doch wieder ueber den Editor laufen
 * muss.
 */

interface Props {
    invoice: Invoice;
    today: string;
    onClose: () => void;
    /** Gebucht und nachgeladen: der frische Beleg fuer die Liste. */
    onDone: (updated: Invoice, payment: InvoicePayment) => void;
}

export const MarkPaidDialog: React.FC<Props> = ({ invoice, today, onClose, onDone }) => {
    const open = openAmountCents(invoice);

    const [valueDate, setValueDate] = useState(today);
    const [amountInput, setAmountInput] = useState(formatCentsPlain(open));
    const [method, setMethod] = useState<PaymentMethod>('BANK_TRANSFER');
    const [reference, setReference] = useState('');
    const [netInput, setNetInput] = useState('');
    const [taxInput, setTaxInput] = useState('');
    /** Was mit dem Rest passiert — nie vorbelegt, siehe RemainderOption. */
    const [remainderKind, setRemainderKind] = useState<RemainderKind | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Gebucht, aber die Liste konnte nicht nachgezogen werden — kein zweiter Versuch. */
    const [stale, setStale] = useState(false);

    const amountCents = centsFromEuroInput(amountInput);
    const remainderCents =
        remainderKind && !isRemainderMethod(method) && amountCents != null
            ? paymentRemainder(invoice, amountCents)
            : 0;

    // Wie im Zahlungsbereich: bei mehreren Steuersaetzen wird die Aufteilung
    // nicht geschaetzt (allocatePayment liefert dann null), sie muss erfasst
    // werden. Umsatzsteuervoranmeldung und DATEV-Export haengen daran.
    const needsAllocation =
        invoice.totals.taxGroups.filter((g) => g.taxableNetCents !== 0).length > 1;

    const issues: ValidationMessage[] = useMemo(
        () =>
            amountCents == null
                ? []
                : [
                      ...validatePayment(
                          {
                              direction: 'INCOMING',
                              valueDate,
                              amountCents,
                              method,
                              reference: reference || undefined,
                          },
                          invoice,
                          today,
                      ),
                      // Skonto oder Einbehalt allein gebucht (als Zahlungsart):
                      // dieselben Pruefungen wie bei der Restwahl.
                      ...(isRemainderMethod(method)
                          ? checkRemainder(invoice, method, amountCents, valueDate)
                          : []),
                  ],
        [amountCents, valueDate, method, reference, invoice, today],
    );
    const blocking = issues.filter((m) => m.level === 'error');

    const handleSave = async () => {
        if (amountCents == null) {
            setError('Der Betrag ist nicht lesbar. Beispiel: 1.190,00');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            let allocation: { allocatedNetCents: number; allocatedTaxCents: number } | undefined;
            if (needsAllocation) {
                const net = centsFromEuroInput(netInput);
                const tax = centsFromEuroInput(taxInput);
                if (net == null || tax == null) {
                    throw new Error(
                        'Die Rechnung enthält mehrere Steuersätze. Bitte Netto und Umsatzsteuer der Zahlung angeben.',
                    );
                }
                allocation = { allocatedNetCents: net, allocatedTaxCents: tax };
            }
            const input = {
                invoiceId: invoice.id,
                direction: 'INCOMING' as const,
                valueDate,
                amountCents,
                method,
                reference: reference || undefined,
                allocation,
            };
            // Mit Skonto oder Einbehalt gehen Zahlung und Rest in EINER
            // Anweisung in die Datenbank — beide oder keine.
            const payment =
                remainderKind && remainderCents > 0
                    ? (
                          await addPaymentWithRemainder(
                              input,
                              { kind: remainderKind, amountCents: remainderCents },
                              invoice,
                              today,
                          )
                      ).payment
                    : await addPayment(input, invoice, today);
            // Ab hier ist das Geld gebucht. Ein Fehlschlag beim Nachladen ist
            // eine ANDERE Lage als eine gescheiterte Buchung: der Dialog bleibt
            // stehen, sperrt aber den Knopf. Sonst bucht der Nutzer dieselbe
            // Zahlung ein zweites Mal — eine Handbuchung hat keine bank_tx_id
            // und damit keinen Doppelbuchungsschutz.
            let updated: Invoice | null;
            try {
                updated = await fetchInvoice(invoice.id);
            } catch (reloadErr) {
                setStale(true);
                setError(
                    `Die Zahlung wurde gebucht, die Liste konnte aber nicht aktualisiert werden (${
                        reloadErr instanceof Error ? reloadErr.message : 'unbekannter Fehler'
                    }). Bitte die Liste neu laden — NICHT erneut buchen.`,
                );
                return;
            }
            if (!updated) {
                setStale(true);
                setError(
                    'Die Zahlung wurde gebucht, der Beleg ist aber nicht mehr abrufbar. Bitte die Liste neu laden — NICHT erneut buchen.',
                );
                return;
            }
            onDone(updated, payment);
        } catch (e) {
            if (e instanceof PaymentValidationError) setError(e.message);
            else
                setError(
                    rechtFehlerOder(e, () =>
                        e instanceof Error ? e.message : 'Die Zahlung konnte nicht gebucht werden.',
                    ),
                );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
            <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                        <Banknote className="h-5 w-5 text-slate-400" />
                        Zahlung buchen — {invoice.invoiceNumber || 'ohne Nummer'}
                    </h2>
                    {/* Waehrend der Buchung ist der Dialog zu. Sonst schliesst
                        der Nutzer ihn, waehrend die Anfrage noch laeuft, oeffnet
                        denselben Beleg erneut — der zeigt noch den alten,
                        offenen Stand — und bucht ein zweites Mal. Eine
                        Handbuchung hat keine bank_tx_id und damit keinen
                        Doppelbuchungsschutz in der Datenbank; beide Zahlungen
                        blieben stehen. */}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        title={busy ? 'Die Buchung läuft noch' : 'Schließen'}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-3 px-5 py-4">
                    <p className="text-xs text-slate-500">
                        Offen: <span className="font-bold text-slate-700">{formatCents(open)}</span>
                        {' von '}
                        {formatCents(invoice.totals.payableCents)}. Der Beleg gilt als bezahlt,
                        sobald die gebuchten Zahlungen den offenen Betrag decken — das rechnet die
                        Datenbank, nicht diese Maske.
                    </p>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Wertstellung</span>
                            <input
                                type="date"
                                value={valueDate}
                                onChange={(e) => setValueDate(e.target.value)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            />
                        </label>
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Betrag €</span>
                            <input
                                inputMode="decimal"
                                value={amountInput}
                                onChange={(e) => setAmountInput(e.target.value)}
                                autoFocus
                                className="w-full rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                            />
                        </label>
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Zahlungsart</span>
                            <select
                                value={method}
                                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            >
                                {(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => (
                                    <option key={m} value={m}>
                                        {METHOD_LABEL[m]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {!isRemainderMethod(method) && (
                        <RemainderOption
                            name={`markpaid-rest-${invoice.id}`}
                            invoice={invoice}
                            paymentCents={amountCents}
                            valueDate={valueDate}
                            value={remainderKind}
                            onChange={setRemainderKind}
                            disabled={busy || stale}
                        />
                    )}

                    {needsAllocation && (
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block text-xs">
                                <span className="mb-0.5 block text-slate-500">davon netto €</span>
                                <input
                                    inputMode="decimal"
                                    value={netInput}
                                    onChange={(e) => setNetInput(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                />
                            </label>
                            <label className="block text-xs">
                                <span className="mb-0.5 block text-slate-500">davon USt €</span>
                                <input
                                    inputMode="decimal"
                                    value={taxInput}
                                    onChange={(e) => setTaxInput(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                />
                            </label>
                            <p className="col-span-2 text-[11px] text-slate-500">
                                Die Rechnung enthält mehrere Steuersätze. Die Aufteilung wird nicht
                                geschätzt — sie muss angegeben werden, weil Umsatzsteuervoranmeldung
                                und DATEV-Export sie brauchen.
                            </p>
                        </div>
                    )}

                    <label className="block text-xs">
                        <span className="mb-0.5 block text-slate-500">Verwendungszweck</span>
                        <input
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                    </label>

                    {issues.length > 0 && (
                        <ul className="space-y-0.5 text-[11px]">
                            {issues.map((m) => (
                                <li
                                    key={m.code}
                                    className={
                                        m.level === 'error' ? 'text-red-600' : 'text-amber-700'
                                    }
                                >
                                    {m.message}
                                </li>
                            ))}
                        </ul>
                    )}

                    {error && (
                        <p className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
                            {error}
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
                    {/* Dieselbe Sperre wie am X: waehrend der Buchung fuehrt
                        kein Weg aus dem Dialog heraus. */}
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={busy}
                        className="!px-3 !py-1 !text-xs"
                    >
                        {stale ? 'Schließen' : 'Abbrechen'}
                    </Button>
                    <Button
                        onClick={() => void handleSave()}
                        loading={busy}
                        disabled={busy || stale || blocking.length > 0 || amountCents == null}
                        className="!px-3 !py-1 !text-xs"
                    >
                        {busy ? 'Wird gebucht …' : 'Zahlung buchen'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
